import {KVStore} from "../../../utils/KVStore";
import {PickUpPhotoType, PickUpPositionType} from "./PickUpPhotoService";

/**
 * 揽收表单草稿：司机没点「完成」就退出页面时，保住已填内容，回到页面原样恢复。
 *
 * 与 PickedUpDataService（前缀 picked_up）的区别：
 * - 这里存的是「未提交」的表单输入，提交成功即删；
 * - 那边存的是「已点完成但网络失败」的作业，由 usePickedUpFailedRetry 自动重传。
 * 两者刻意不共享前缀：KVStore.getAllItems 按 startsWith 匹配，若草稿前缀以 picked_up 开头，
 * 草稿会被重传队列捞走并自动提交——等于把没签完字的单子发给后端。
 */

// 与 PickedUpData.type 取值保持一致，避免同一概念两套词
export type PickUpDraftType = 'pickedUp' | 'failed' | 'failedToSec';

export interface PickUpFormDraft {
    appointmentId: string;
    type: PickUpDraftType;
    updatedAt: number;
    // 以下均为可选：不同表单填的字段不同，后续扩到揽收失败页时不必改结构
    photos?: Record<PickUpPositionType, PickUpPhotoType | undefined>;
    // 揽收数量不单独存：它只跟最后一次 BOL 签字走，回填时取 signedQuantity（见下方签字基准），
    // 单位与 signedUnit 一致才恢复。单独存一份会让「换单位后被空值覆盖」「未签字的旧值被当成有效」
    // 这两类问题反复出现
    // 揽收备注：司机现场填写，落到 BOL 的 Notes 栏
    pickupNote?: string;
    // 运单号与「无法扫描」原因互斥，最多一边有值
    trackingNumbers?: string[];
    // 弱网/无网下跳过校验入列的运单号（列表里标红点）。必须跟 trackingNumbers 一起存：
    // 只恢复单号不恢复这个集合，重进页面会显示成全部已校验，等于骗司机
    unverifiedTrackingNumbers?: string[];
    unscannableReason?: string | null;
    // 取位当时的司机位置，必须跟着扫描记录一起存：只存记录不存位置的话，
    // 中途退出再进来会恢复出「有运单号/有原因但没坐标」的残缺数据
    scanLatitude?: number | null;
    scanLongitude?: number | null;
    bolSignatures?: {shipper: string | null; driver: string | null};
    // 签字时的数量、单位与揽收备注（单据的 Qty / Container / Notes 三栏），签字有效性的基准，
    // 不存则回填后签字会被判为无效
    signedQuantity?: string | null;
    signedUnit?: string | null;
    signedRemark?: string | null;
    // BOL 单据上的日期。必须存：不存的话跨零点重进页面会变成新的一天，
    // 而签字仍有效，归档的图就成了「新日期 + 旧签字」，与司机实际签的那张单对不上
    bolDate?: string;
    // 揽收失败页的签字基准：签字当时的失败原因
    signedReason?: string | null;
    onSiteContactName?: string;
    onSiteContactPhone?: string;
    onSiteNote?: string;
}

const PICKUP_FORM_DRAFT_STORAGE_KEY = 'pickup_form_draft';
// 揽收是当日作业：超过 24 小时的草稿直接丢弃，避免几天后重开预约单时回填出陈旧的签字
const DRAFT_EXPIRE_MS = 24 * 60 * 60 * 1000;
const ALL_DRAFT_TYPES: PickUpDraftType[] = ['pickedUp', 'failed', 'failedToSec'];

class PickUpDraftService {

    private kvStore: KVStore;

    constructor() {
        this.kvStore = new KVStore(PICKUP_FORM_DRAFT_STORAGE_KEY);
    }

    // key 带类型：同一预约单的揽收成功/失败草稿互不覆盖
    private draftKey(appointmentId: string, type: PickUpDraftType): string {
        return `${appointmentId}_${type}`;
    }

    /** 保存草稿，失败只记日志不打扰司机 */
    async saveDraft(draft: PickUpFormDraft): Promise<boolean> {
        if (!draft.appointmentId) {
            return false;
        }
        return this.kvStore.setItem(this.draftKey(draft.appointmentId, draft.type), {
            ...draft,
            updatedAt: Date.now(),
        });
    }

    /** 读取草稿，已过期则顺手删除并返回 null */
    async getDraft(appointmentId: string, type: PickUpDraftType): Promise<PickUpFormDraft | null> {
        if (!appointmentId) {
            return null;
        }
        const draft = await this.kvStore.getItem<PickUpFormDraft>(this.draftKey(appointmentId, type));
        if (!draft) {
            return null;
        }
        if (Date.now() - (draft.updatedAt || 0) > DRAFT_EXPIRE_MS) {
            await this.removeDraft(appointmentId, type);
            return null;
        }
        return draft;
    }

    async removeDraft(appointmentId: string, type: PickUpDraftType): Promise<boolean> {
        if (!appointmentId) {
            return false;
        }
        return this.kvStore.removeItem(this.draftKey(appointmentId, type));
    }

    /** 预约单已提交（无论走成功还是失败）时，清掉它名下所有类型的草稿 */
    async removeDraftsByAppointment(appointmentId: string): Promise<void> {
        if (!appointmentId) {
            return;
        }
        await Promise.all(ALL_DRAFT_TYPES.map((type) => this.removeDraft(appointmentId, type)));
    }
}

const pickUpDraftService = new PickUpDraftService();
export default pickUpDraftService;
